import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'Ruth H.',
    role: 'Homeowner, Tysons VA',
    text: 'We\'ve used AXIS Cleaning Services multiple times over the past few years. They are so quick to respond, have trustworthy, respectable staff dedicated to ensuring the job is not just done, but done RIGHT! Pricing is the best you will find in this area & the solid communication involved in scheduling, providing estimates, & even follow up is outstanding. Cannot recommend them enough!',
    rating: 5,
    source: 'Google',
    logo: '/google.svg',
  },
  {
    name: 'Jean D.',
    role: 'Homeowner, Gaithersburg MD',
    text: 'I have been using Axis Cleaning Services for several months now and am very pleased with the service. The ladies that come are pleasant and efficient and leave my house in sparkling condition. Their pricing is reasonable for this area and they are very accommodating when it comes to scheduling. I would confidently recommend them!',
    rating: 5,
    source: 'HomeAdvisor',
    logo: 'https://imgs.search.brave.com/7yQ1GNPwlwp3X_eage7H3rDNmg15ptp-qsI2S20nJ2M/rs:fit:500:0:1:0/g:ce/aHR0cHM6Ly9pbWFn/ZXMuc2Vla2xvZ28u/Y29tL2xvZ28tcG5n/LzMzLzIvaG9tZWFk/dmlzb3ItbG9nby1w/bmdfc2Vla2xvZ28t/MzM5NTM4LnBuZw',
  },
  {
    name: 'Garo T.',
    role: 'Homeowner, Ijamsville MD',
    text: 'I just started using Axis Cleaning Services a month ago after a coworker recommended them. The work has been immaculate and everyone involved has been super easy to deal with so far.',
    rating: 5,
    source: 'Google',
    logo: '/google.svg',
  },
  {
    name: 'Maria L.',
    role: 'Homeowner, Rockville MD',
    text: "AXIS transformed our house and it looked brand new. They were thorough, on time, and very professional. We've continued with bi-weekly service and couldn't be happier. Highly recommend for anyone looking for reliable cleaning!",
    rating: 5,
    source: 'Google',
    logo: '/google.svg',
  },
  {
    name: 'David K.',
    role: 'Office Manager, Germantown MD',
    text: 'We switched to AXIS for our office cleaning and the difference is night and day. Our employees have noticed the improvement and the team is always reliable. Scheduling is easy and they work around our hours. Great service at a fair price.',
    rating: 5,
    source: 'Google',
    logo: '/google.svg',
  },
  {
    name: 'Priya S.',
    role: 'Homeowner, North Potomac MD',
    text: "Excellent cleaning service from start to finish! The team was incredibly detailed and left every corner spotless. Communication was perfect throughout. We've since signed up for regular cleaning and couldn't be more satisfied.",
    rating: 5,
    source: 'HomeAdvisor',
    logo: 'https://imgs.search.brave.com/7yQ1GNPwlwp3X_eage7H3rDNmg15ptp-qsI2S20nJ2M/rs:fit:500:0:1:0/g:ce/aHR0cHM6Ly9pbWFn/ZXMuc2Vla2xvZ28u/Y29tL2xvZ28tcG5n/LzMzLzIvaG9tZWFk/dmlzb3ItbG9nby1w/bmdfc2Vla2xvZ28t/MzM5NTM4LnBuZw',
  },
];

export default function Testimonials() {
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true, margin: '-100px' });

  return (
    <section id="reviews" className="relative py-24 sm:py-32 bg-sand-50 overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-10 right-10 w-72 h-72 bg-axis-100/20 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-10 w-56 h-56 bg-sand-200/20 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto section-padding relative z-10">
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-axis-50 rounded-full px-4 py-1.5 mb-6">
            <span className="text-sm font-medium text-axis-700">Customer Reviews</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-axis-950 leading-tight mb-4">
            What Our Clients
            <br />
            <span className="gradient-text italic">Are Saying</span>
          </h2>
          <p className="text-gray-500 text-lg">
            Real reviews from our valued clients on Google & HomeAdvisor.
          </p>
        </motion.div>

        {/* Testimonial Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, i) => {
            const ref = useRef(null);
            const isInView = useInView(ref, { once: true, margin: '-60px' });

            return (
              <motion.div
                key={i}
                ref={ref}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="group"
              >
                <div className="bg-white rounded-3xl p-8 border border-axis-50 hover:shadow-2xl hover:shadow-axis-900/8 transition-all duration-500 hover:-translate-y-1 h-full flex flex-col">
                  {/* Quote Icon */}
                  <div className="mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-axis-50 flex items-center justify-center">
                      <Quote className="w-6 h-6 text-axis-400" />
                    </div>
                  </div>

                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: testimonial.rating }).map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-sand-400 text-sand-400" />
                    ))}
                  </div>

                  {/* Text */}
                  <p className="text-gray-600 leading-relaxed mb-6 flex-1">
                    "{testimonial.text}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-3 pt-4 border-t border-axis-50">
                    <div className="w-10 h-10 rounded-full clean-gradient flex items-center justify-center text-white font-display font-semibold text-sm">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-sm font-semibold text-axis-900">{testimonial.name}</div>
                      <div className="text-xs text-gray-500">{testimonial.role}</div>
                    </div>
                    {testimonial.logo && (
                      <img
                        src={testimonial.logo}
                        alt={testimonial.source}
                        className={`object-contain flex-shrink-0 ${testimonial.source === 'HomeAdvisor' ? 'h-24 w-auto' : 'h-6'}`}
                      />
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
